import { i as isPlainObject, c as clone } from './fexios.DtQgiOCE.mjs';

var FexiosQueryBuilder;
((FexiosQueryBuilder2) => {
  FexiosQueryBuilder2.makeSearchParams = (params) => {
    if (!params) {
      return new URLSearchParams();
    }
    if (params instanceof URLSearchParams) {
      return params;
    }
    if (typeof params !== "object" || params?.constructor !== Object) {
      throw new TypeError("only plain object is supported");
    }
    const sp = new URLSearchParams();
    const appendValue = (k, v) => {
      if (v === void 0 || v === null) return;
      sp.append(k, v);
    };
    const setValue = (k, v) => {
      if (v === void 0 || v === null) return;
      sp.set(k, v);
    };
    const handleNested = (prefix, val) => {
      if (val === void 0 || val === null) return;
      if (Array.isArray(val)) {
        for (const item of val) appendValue(prefix, item?.toString());
        return;
      }
      if (typeof val === "object" && val.constructor === Object) {
        for (const [k, v] of Object.entries(val)) {
          if (v === void 0 || v === null) continue;
          const isBracketArrayKey = k.endsWith("[]");
          const cleanKey = isBracketArrayKey ? k.slice(0, -2) : k;
          const nextPrefix = `${prefix}[${cleanKey}]`;
          if (isBracketArrayKey) {
            const arrayKey = `${nextPrefix}[]`;
            if (Array.isArray(v)) {
              for (const item of v) appendValue(arrayKey, item?.toString());
            } else if (typeof v === "object" && v !== null && v.constructor === Object) {
              handleNested(`${nextPrefix}[]`, v);
            } else {
              appendValue(arrayKey, v?.toString());
            }
          } else {
            if (Array.isArray(v)) {
              for (const item of v) appendValue(nextPrefix, item?.toString());
            } else if (typeof v === "object" && v !== null && v.constructor === Object) {
              handleNested(nextPrefix, v);
            } else {
              setValue(nextPrefix, v?.toString());
            }
          }
        }
        return;
      }
      setValue(prefix, val?.toString());
    };
    for (const [key, value] of Object.entries(params)) {
      handleNested(key, value);
    }
    return sp;
  };
  FexiosQueryBuilder2.makeQueryString = (query) => {
    return (0, FexiosQueryBuilder2.makeSearchParams)(query).toString();
  };
  FexiosQueryBuilder2.makeURL = (url, params, hash, base) => {
    const fallbackBase = typeof window !== "undefined" && window.location?.origin || "http://localhost";
    const u = typeof url === "string" ? new URL(url, base ?? fallbackBase) : new URL(url);
    const existingParams = (0, FexiosQueryBuilder2.toQueryRecord)(u.searchParams);
    const mergedRecord = (0, FexiosQueryBuilder2.mergeQueries)(existingParams, params || {});
    const mergedParams = (0, FexiosQueryBuilder2.makeSearchParams)(mergedRecord);
    u.search = mergedParams.toString();
    if (typeof hash !== "undefined") u.hash = hash;
    return u;
  };
  FexiosQueryBuilder2.toQueryRecord = (input) => {
    if (typeof input === "string") {
      input = (0, FexiosQueryBuilder2.fromString)(input);
    }
    const output = {};
    const parseKey = (key) => {
      if (!key.includes("[")) return { path: [key], forceArray: false };
      const base = key.slice(0, key.indexOf("["));
      const parts = [base];
      const re = /\[([^\]]*)\]/g;
      let m;
      let forceArray = false;
      let lastWasEmpty = false;
      while (m = re.exec(key)) {
        if (m[1] === "") {
          forceArray = true;
          lastWasEmpty = true;
        } else {
          parts.push(m[1]);
          lastWasEmpty = false;
        }
      }
      if (forceArray && lastWasEmpty) {
        parts[parts.length - 1] = parts[parts.length - 1] + "[]";
      }
      return { path: parts, forceArray };
    };
    const setDeep = (obj, path, value, forceArray) => {
      let cur = obj;
      for (let i = 0; i < path.length; i++) {
        const k = path[i];
        const last = i === path.length - 1;
        if (last) {
          if (forceArray) {
            if (cur[k] === void 0) cur[k] = [value];
            else if (Array.isArray(cur[k])) cur[k].push(value);
            else cur[k] = [cur[k], value];
          } else {
            if (cur[k] === void 0) cur[k] = value;
            else if (Array.isArray(cur[k])) cur[k].push(value);
            else cur[k] = [cur[k], value];
          }
        } else {
          if (cur[k] === void 0 || typeof cur[k] !== "object" || Array.isArray(cur[k])) {
            cur[k] = {};
          }
          cur = cur[k];
        }
      }
    };
    for (const [rawKey, val] of input.entries()) {
      const { path, forceArray } = parseKey(String(rawKey));
      setDeep(output, path, val?.toString(), forceArray);
    }
    return output;
  };
  FexiosQueryBuilder2.fromString = (s) => {
    const t = s.trim();
    if (!t) return new URLSearchParams();
    if (t.startsWith("?")) return new URLSearchParams(t.slice(1));
    const qIndex = t.indexOf("?");
    if (qIndex >= 0) {
      const hashIndex = t.indexOf("#", qIndex + 1);
      const query = t.slice(qIndex + 1, hashIndex >= 0 ? hashIndex : void 0);
      return new URLSearchParams(query);
    }
    return new URLSearchParams(t);
  };
  FexiosQueryBuilder2.mergeQueries = (...incomes) => {
    const output = {};
    for (const input of incomes) {
      if (input == null) continue;
      mergeOne(output, toPlain(input));
    }
    return output;
  };
  function toPlain(src) {
    if (!src) return {};
    if (src instanceof URLSearchParams || src instanceof FormData || src instanceof Map)
      return (0, FexiosQueryBuilder2.toQueryRecord)(src);
    if (typeof src === "string") return (0, FexiosQueryBuilder2.toQueryRecord)((0, FexiosQueryBuilder2.fromString)(src));
    if (isPlainObject(src)) return src;
    throw new TypeError(
      `unsupported type transformation, got: ${Object.prototype.toString.call(
        src
      )}`
    );
  }
  function mergeOne(target, patch) {
    for (const [k, v] of Object.entries(patch)) {
      if (v === void 0) continue;
      if (v === null) {
        delete target[k];
        continue;
      }
      const cur = target[k];
      if (isPlainObject(cur) && isPlainObject(v)) {
        mergeOne(cur, v);
      } else {
        target[k] = clone(v);
      }
    }
  }
})(FexiosQueryBuilder || (FexiosQueryBuilder = {}));

export { FexiosQueryBuilder as F };
//# sourceMappingURL=fexios.TxSvO_qH.mjs.map
